<template>
  <div class="student-verification">
    <h2 class="section-title">Verify Your Information</h2>
    
    <div v-if="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p>Loading student information...</p>
    </div>
    
    <div v-else-if="error" class="error-container">
      <div class="alert alert-danger">
        {{ error }}
      </div>
      <button class="btn btn-secondary btn-lg" @click="goBack">
        Go Back
      </button>
    </div>
    
    <div v-else-if="!student && !isAnonymousTransaction" class="no-student">
      <div class="alert alert-warning">
        No student information available. Please go back and try again.
      </div>
      <button class="btn btn-secondary btn-lg" @click="goBack">
        Go Back
      </button>
    </div>
    
    <div v-else class="student-info-card">
      <!-- Priority Notice Banner -->
      <div v-if="isPriority" class="priority-notice">
        <i class="fas fa-star me-2"></i> This transaction is priority
      </div>
      
      <div class="card">
        <div class="card-header">
          <h3 v-if="student" style="color: #fff;" class="student-name">{{ student.lastname }}, {{ student.firstname }} {{ student.middlename ? student.middlename : '' }}</h3>
          <h3 v-else style="color: #fff;" class="student-name">Guest / {{ selectedTransactionType?.display_name || 'Anonymous Transaction' }}</h3>
          <div v-if="student" class="student-id">ID: {{ student.student_id_number }}</div>
        </div>
        <div class="card-body">
          <div class="row">
            <!-- <div class="col-md-4 student-photo">
              <img 
                :src="student.photo_url || '/img/default-avatar.png'" 
                alt="Student Photo" 
                class="img-fluid"
              />
            </div> -->
            <div class="col-md-8" style="width: 100%;">
              <div class="student-details">
                <div v-if="isAnonymousTransaction" class="detail-item">
                  <span class="detail-label">Status:</span> 
                  <span class="detail-value">Guest / Information to be determined</span>
                </div>
                <template v-else-if="student">
                  <div class="detail-item">
                    <span class="detail-label">Course:</span>
                    <span class="detail-value">{{ student.course || 'N/A' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Year Level:</span>
                    <span class="detail-value">{{ student.year ? getYearLevelName(student.year) : 'N/A' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Section:</span>
                    <span class="detail-value">{{ student.section || 'N/A' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Course & Year:</span>
                    <span class="detail-value">{{ student.course || 'N/A' }} {{ student.year || '' }} {{ student.section || '' }}</span>
                  </div>
                </template>
                <div class="detail-item" v-if="transactionAmount > 0">
                  <span class="detail-label">Amount to Pay:</span>
                  <span class="detail-value">₱{{ transactionAmount.toFixed(2) }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div class="transaction-summary mt-4">
            <h4>Transaction Summary</h4>
            <div class="transaction-details">
              <div class="transaction-type">
                <span class="transaction-label">Transaction Type:</span>
                <span class="transaction-value">{{ selectedTransactionType.display_name }}</span>
              </div>
              <div class="transaction-fee" v-if="selectedTransactionType.name === 'new_student_admission' && entranceExamFee > 0">
                <span class="transaction-label">Entrance Exam Fee:</span>
                <span class="transaction-value">₱{{ entranceExamFee.toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="card-footer">
          <div class="action-buttons">
            <button 
              class="btn btn-success btn-lg w-100" 
              @click="confirmAndCreateTransaction"
              :disabled="loading"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ loading ? 'Processing...' : 'Confirm Information' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { settingsApi } from '@/services/api';

export default {
  name: 'StudentVerification',
  emits: ['step-back', 'step-complete'],
  props: {
    config: {
      type: Object,
      default: () => ({
        verificationMethod: 'id_number',
        allowManualEntry: true
      })
    },
    transactionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      localLoading: false,
      localError: null,
      entranceExamFee: 0,
      loadingFee: false
    };
  },
  computed: {
    ...mapGetters({
      student: 'kiosk/getStudent',
      selectedTransactionType: 'kiosk/getSelectedTransactionType',
      isPriority: 'kiosk/isPriorityMode',
      storeLoading: 'kiosk/isLoading',
      storeError: 'kiosk/getError',
      transactionAmount: 'kiosk/getTransactionAmount'
    }),
    isAnonymousTransaction() {
      // Return true if we don't have a student but we have a valid transaction type
      if (this.student) return false;
      return !!this.selectedTransactionType;
    },
    loading() {
      return this.localLoading || this.storeLoading;
    },
    error() {
      return this.localError || this.storeError;
    }
  },
  async created() {
    // Load entrance exam fee
    await this.loadEntranceExamFee();
    
    // Check if we have student data, if not try to get it from transactionData
    if (!this.student && this.transactionData && this.transactionData.studentId) {
      this.fetchStudentData(this.transactionData.studentId);
    }
  },
  methods: {
    ...mapActions({
      createTransaction: 'kiosk/createTransaction',
      fetchStudentByIdNumber: 'kiosk/fetchStudentByIdNumber'
    }),
    
    async loadEntranceExamFee() {
      if (this.loadingFee) return;
      
      this.loadingFee = true;
      try {
        const response = await settingsApi.getKioskSettings();
        if (response.data && response.data.entrance_exam_fee) {
          this.entranceExamFee = parseFloat(response.data.entrance_exam_fee) || 0;
        }
      } catch (error) {
        console.error('Error loading entrance exam fee:', error);
        this.entranceExamFee = 0;
      } finally {
        this.loadingFee = false;
      }
    },
    async fetchStudentData(studentId) {
      if (!studentId) return;
      
      this.localLoading = true;
      try {
        await this.fetchStudentByIdNumber(studentId);
      } catch (error) {
        console.error('Error fetching student data:', error);
        this.localError = 'Failed to load student data. Please try again.';
      } finally {
        this.localLoading = false;
      }
    },
    goBack() {
      this.$emit('step-back');
    },
    async confirmAndCreateTransaction() {
      console.group('Create Transaction Flow');
      this.localLoading = true;
      
      // If the transaction was already created earlier in the flow (e.g. at the
      // amount input step), reuse it instead of allocating a new queue number.
      if (this.transactionData && this.transactionData.transactionId) {
        console.log('Reusing existing transaction:', this.transactionData.transactionId);
        this.$emit('step-complete', {
          transactionId: String(this.transactionData.transactionId)
        });
        return;
      }
      
      try {
        console.log('Starting transaction creation process');
        console.log('Transaction data:', {
          student_id: this.student ? this.student.id : null,
          transaction_type_id: this.selectedTransactionType.id,
          is_priority: this.isPriority,
          amount: this.transactionAmount
        });
        
        const response = await this.createTransaction();
        console.log('Transaction created successfully:', response);
        
        if (!response || !response.id) {
          console.error('Transaction created but missing ID:', response);
          throw new Error('Transaction created but missing ID');
        }
        
        // Emit step-complete event with transaction ID
        this.$emit('step-complete', {
          transactionId: response.id.toString()
        });
      } catch (error) {
        console.error('Failed to create transaction:', error);
        console.error('Error details:', error.response ? error.response.data : 'No response data');
        // Show a more descriptive error message to the user
        if (error.response && error.response.status === 500) {
          this.$store.commit('kiosk/SET_ERROR', 'Server error occurred. Please try again or contact support.');
        }
      } finally {
        this.localLoading = false;
        console.groupEnd();
      }
    },
    getYearLevelName(year) {
      const yearNum = parseInt(year);
      
      switch(yearNum) {
        case 1:
          return '1st Year';
        case 2:
          return '2nd Year';
        case 3:
          return '3rd Year';
        case 4:
          return '4th Year';
        default:
          return `${yearNum}th Year`;
      }
    }
  }
};
</script>

<style scoped>
.student-verification {
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.section-title {
  text-align: center;
  margin-bottom: 10px;
  font-family: var(--font-heading);
  font-weight: 700;
  color: var(--brand-dark);
  font-size: 2.4rem;
}

.loading-container,
.error-container,
.no-student {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
}

.priority-notice {
  background-color: var(--brand-accent);
  color: var(--brand-dark);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  width: 100%;
  text-align: center;
  font-size: 1.3rem;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(255, 200, 34, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}

.student-info-card {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.card {
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: none;
}

.card-header {
  background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-dark) 120%);
  color: white;
  padding: 2rem;
}

.student-name {
  font-family: var(--font-heading);
  font-size: 2rem;
  margin: 0;
  font-weight: 600;
}

.student-id {
  font-size: 1.3rem;
  opacity: 0.9;
  margin-top: 0.5rem;
}

.card-body {
  padding: 2rem;
}

.student-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-item {
  display: flex;
  align-items: baseline;
  font-size: 1.2rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.75rem;
}

.detail-label {
  font-weight: bold;
  width: 150px;
  color: var(--secondary-text);
}

.detail-value {
  margin-left: 2rem;
  flex: 1;
  color: #333;
  font-weight: 500;
}

.transaction-summary {
  padding: 1.5rem;
  margin-top: 1.5rem;
  background-color: var(--brand-surface);
  border-radius: 8px;
  border: 1px solid var(--brand-border);
}

.transaction-summary h4 {
  font-family: var(--font-heading);
  color: var(--brand-primary);
  margin-bottom: 1.25rem;
  font-weight: 700;
  font-size: 1.4rem;
  margin-bottom: 1rem;
}

.transaction-details {
  /* display: flex; */
  display: block;
  justify-content: space-between;
  align-items: center;
  background-color: var(--brand-ice);
  padding: 1rem;
  border-radius: 8px;
}

.transaction-type, .transaction-fee {
  font-size: 1.2rem;
}

.transaction-label {
  font-weight: bold;
  margin-right: 0.5rem;
  color: var(--secondary-text);
}

.transaction-value {
  color: var(--brand-dark);
  font-weight: 500;
}

.card-footer {
  background-color: var(--brand-surface);
  border-top: 1px solid var(--brand-border);
  padding: 2rem;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
}

.btn-lg {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-lg:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.btn-secondary {
  background-color: var(--brand-dark);
  border-color: var(--brand-dark);
}

.btn-secondary:hover {
  background-color: var(--brand-primary);
  border-color: var(--brand-primary);
}

.btn-success {
  background-color: var(--brand-primary);
  border-color: var(--brand-primary);
  border-radius: 12px;
}

.btn-success:hover {
  background-color: var(--brand-cyan);
  border-color: var(--brand-cyan);
}

/* Portrait mode adjustments */
@media screen and (orientation: portrait) {
  .student-verification {
    overflow: hidden;
    padding: 1rem; 
    height: 90vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  
  .section-title {
    font-size: 2.8rem;
    margin-bottom: 10px;
  }

  .student-info-card {
    width: 100%;
    max-width: none;
  }
  
  .card {
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
  }
  
  .card-header {
    padding: 2.5rem;
  }
  
  .student-name {
    font-size: 2.6rem;
  }
  
  .student-id {
    font-size: 1.6rem;
    margin-top: 0.8rem;
  }
  
  .card-body {
    padding: 2.5rem;
  }
  
  .detail-item {
    font-size: 1.5rem;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }
  
  .detail-label {
    width: 180px;
  }
  
  .detail-value {
    font-weight: 600;
  }
  
  .transaction-summary {
    padding-top: 2rem;
    margin-top: 2rem;
  }
  
  .transaction-summary h4 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
  
  .transaction-details {
    padding: 1.5rem;
    border-radius: 10px;
  }
  
  .transaction-type, .transaction-fee {
    font-size: 1.5rem;
  }
  
  .priority-notice {
    font-size: 1.8rem;
    padding: 1.5rem;
    border-radius: 12px;
    margin-bottom: 2rem;
  }
  
  .card-footer {
    padding: 2.5rem;
  }
  
  .btn-lg {
    font-size: 1.6rem;
    padding: 1rem 2.5rem;
    border-radius: 10px;
  }
}

/* Additional adjustments for different screen sizes */
@media screen and (max-height: 800px) and (orientation: portrait) {
  .student-verification {
    padding: 1rem 1rem;
  }
  
  .section-title {
    font-size: 2.4rem;
    margin-bottom: 10px;
  }
  
  .student-info-card {
    width: 100%;
  }
}

@media screen and (min-height: 1200px) and (orientation: portrait) {
  .student-verification {
    padding: 1rem 1rem;
  }
  
  .section-title {
    font-size: 3.2rem;
    margin-bottom: 10px;
  }
  
  .student-info-card {
    width: 100%;
  }
  
  .student-name {
    font-size: 3rem;
  }
  
  .student-id {
    font-size: 1.8rem;
  }
  
  .detail-item {
    font-size: 1.7rem;
  }
  
  .btn-lg {
    font-size: 1.8rem;
    padding: 1.2rem 3rem;
  }
}
</style> 